import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { AuthContext } from "../../context/AuthContext";
import { createSchedule, expandParticipantsForMeeting, sendMail, cancelMail } from "../../api/scheduleApi";
import { getCurrentUser } from "../../api/common";
import CommonDatePicker from "../../components/DatePicker/CommonDatePicker";
import "react-datepicker/dist/react-datepicker.css";
import OrgTreeSelect from "../../components/OrgSelect/OrgTreeSelect";

const toDateText = (d) => {
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const toTimeText = (d) => {
  if (!d) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}:00`;
};

const roundToInterval = (date, interval = 10) => {
  const ms = 1000 * 60 * interval;
  return new Date(Math.ceil(date.getTime() / ms) * ms);
};

function ScheduleCard() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  // 활성 탭
  const [activeTab, setActiveTab] = useState('schedule');

  // 탭 변경 시 입력값 초기화 함수
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const now = new Date();
    if (tab === 'schedule') {
      setSchedTitle('');
      setSchedStart(roundToInterval(now, 10));
      setSchedEnd(roundToInterval(new Date(now.getTime() + 60 * 60 * 1000), 10));
      setSelectedDept(null);
      setSelectedTargets([]);
    } else {
      setCollabTitle('');
      setCollabStart(roundToInterval(now, 10));
      setCollabEnd(roundToInterval(new Date(now.getTime() + 60 * 60 * 1000), 10));
      setCollabSendMailYn('N');
      setCollabSearchTerm('');
      setCollabSearchUsers([]);
      setCollabSearchDepts([]);
      setShowCollabSearchModal(false);
      setShowCollabOrgPopup(false);
      setCollabParticipants([]);
      setIsCollabSubmitting(false);
    }
  };

  const [orgTargetType, setOrgTargetType] = useState('all'); // 조직도 타겟 타입 상태

  // ── 업무일정 상태 ────────────────────────────────────────
  const [schedTitle, setSchedTitle] = useState('');
  const [schedStart, setSchedStart] = useState(() => roundToInterval(new Date(), 10));
  const [schedEnd, setSchedEnd] = useState(() => roundToInterval(new Date(Date.now() + 60 * 60 * 1000), 10));
  const [departments, setDepartments] = useState([]);
  const [topDept, setTopDept] = useState(null);
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [userInfo, setUserInfo] = useState(null);

  // ── 공유/회의일정 상태 ───────────────────────────────────
  const [collabTitle, setCollabTitle] = useState('');
  const [collabStart, setCollabStart] = useState(() => roundToInterval(new Date(), 10));
  const [collabEnd, setCollabEnd] = useState(() => roundToInterval(new Date(Date.now() + 60 * 60 * 1000), 10));
  const [collabSendMailYn, setCollabSendMailYn] = useState('N');
  const [collabSearchTerm, setCollabSearchTerm] = useState('');
  const [collabSearchUsers, setCollabSearchUsers] = useState([]);
  const [collabSearchDepts, setCollabSearchDepts] = useState([]);
  const [showCollabSearchModal, setShowCollabSearchModal] = useState(false);
  const [showCollabOrgPopup, setShowCollabOrgPopup] = useState(false);
  const [collabParticipants, setCollabParticipants] = useState([]);
  const [isCollabSubmitting, setIsCollabSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    getCurrentUser(token)
      .then((info) => setUserInfo(info))
      .catch(() => { });
  }, [token]);

  // 업무일정용 부서 목록 로드
  useEffect(() => {
    if (!user?.userid || !token) return;
    fetch(`/api/auth/user/${user.userid}/departments`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        const deptArray = Array.isArray(data) ? data : [data];
        const expanded = deptArray
          .filter(d => d && d.full_dept_name && d.full_dept_code)
          .flatMap(dept => {
            const names = dept.full_dept_name.split(" > ");
            const codes = dept.full_dept_code.split(" > ");
            return names.map((name, idx) => ({
              dept_name: name,
              dept_code: codes[idx] ?? "",
              full_dept_name: names.slice(0, idx + 1).join(" > "),
              full_dept_code: codes.slice(0, idx + 1).join(" > ")
            }));
          });
        const unique = expanded.filter(
          (item, idx, arr) => arr.findIndex(d => d.dept_code === item.dept_code) === idx
        );
        setDepartments(unique);
        setTopDept(unique[0] || null);
        setSelectedDept(null);
      })
      .catch(() => { });
  }, [user, token]);

  // 업무일정 저장
  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    if (!schedTitle.trim()) { alert("제목을 입력하세요."); return; }
    if (!schedStart || !schedEnd) { alert("일정일시를 선택하세요."); return; }
    if (schedEnd <= schedStart) { alert("종료일시는 시작일시보다 늦어야 합니다."); return; }
    if (selectedTargets.length === 0) { alert("부서를 선택하세요."); return; }
    const hasTopLevelTarget = selectedTargets.some(
      p => p.target_id === topDept?.dept_code
    );
    try {
      await createSchedule({
        title: schedTitle,
        writer_id: user?.userid,
        writer_name: user?.name,
        writer_post: userInfo?.position || null,
        dept_code: user?.dept_code || null,
        dept_name: userInfo?.dept_name || null,
        start_date: toDateText(schedStart),
        start_time: toTimeText(schedStart),
        end_date: toDateText(schedEnd),
        end_time: toTimeText(schedEnd),
        event_type: "W",
        participants: selectedTargets.map(t => ({
          target_from: "W",
          target_type: t.target_type,
          target_id: t.target_id,
          target_name: t.target_name,
          disp_target_name: t.disp_target_name
        })),
        content: "",
        status: hasTopLevelTarget ? "request" : "register",
        deletedFileIds: []
      }, token);
      alert("등록되었습니다.");
      const now = new Date();
      setSchedTitle('');
      setSchedStart(roundToInterval(now, 10));
      setSchedEnd(roundToInterval(new Date(now.getTime() + 60 * 60 * 1000), 10));
      setSelectedDept(null);
      setSelectedTargets([]);
    } catch (err) {
      alert("일정 등록 중 오류가 발생했습니다.");
    }
  };

  // 공유/회의일정 관련 함수
  const makeParticipantKey = (target) => `${target.target_type}:${String(target.target_id)}`;
  const normalizeSearchUser = (u) => ({
    target_type: 'user',
    target_id: String(u.userid || u.id || ''),
    target_name: u.name || '',
    disp_target_name: `${u.name || ''}${u.position ? ` / ${u.position}` : ''}${u.dept_name ? ` (${u.dept_name})` : ''}`,
    checked: false
  });
  const normalizeSearchDept = (d) => ({
    target_type: 'dept',
    target_id: String(d.dept_code || ''),
    target_name: d.dept_name || '',
    disp_target_name: d.full_dept_name || d.dept_name || '',
    checked: false
  });
  const addCollabParticipant = (target) => {
    if (!target?.target_id || !target?.target_type) return;
    setCollabParticipants(prev => {
      const exists = prev.some(
        p => String(p.target_id) === String(target.target_id) && p.target_type === target.target_type
      );
      if (exists) return prev;
      return [...prev, {
        target_type: target.target_type,
        target_id: String(target.target_id),
        target_name: target.target_name || '',
        disp_target_name: target.disp_target_name || target.target_name || '',
        checked: false
      }];
    });
  };

  const handleCollabSearch = async (keyword) => {
    const term = (keyword || '').trim();
    if (!term) { alert('검색어를 입력하세요.'); return; }
    if (term.length < 2) { alert('정확한 검색을 위해 2자 이상 입력해 주세요.'); return; }
    try {
      const res = await fetch(`/api/org/search?keyword=${encodeURIComponent(term)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-com-code': user?.M_ComCode || sessionStorage.getItem('M_ComCode')
        }
      });
      const data = await res.json();
      const users = Array.isArray(data.users) ? data.users : [];
      const depts = Array.isArray(data.depts) ? data.depts : [];
      if (users.length === 0 && depts.length === 0) {
        alert(data.message || '검색 결과가 없습니다.');
        return;
      }
      if (users.length === 1 && depts.length === 0) { addCollabParticipant(normalizeSearchUser(users[0])); setCollabSearchTerm(''); return; }
      if (depts.length === 1 && users.length === 0) { addCollabParticipant(normalizeSearchDept(depts[0])); setCollabSearchTerm(''); return; }
      setCollabSearchUsers(users);
      setCollabSearchDepts(depts);
      setShowCollabSearchModal(true);
    } catch (err) {
      alert('검색 중 오류가 발생했습니다.');
    }
  };
  const toggleSearchModalItem = (type, id) => {
    if (type === 'user') {
      setCollabSearchUsers(prev => prev.map(u =>
        String(u.userid) === String(id) ? { ...u, checked: !u.checked } : u
      ));
      return;
    }
    setCollabSearchDepts(prev => prev.map(d =>
      String(d.dept_code) === String(id) ? { ...d, checked: !d.checked } : d
    ));
  };
  const handleConfirmCollabSearchModal = () => {
    collabSearchUsers.filter(u => u.checked).forEach(u => addCollabParticipant(normalizeSearchUser(u)));
    collabSearchDepts.filter(d => d.checked).forEach(d => addCollabParticipant(normalizeSearchDept(d)));
    setShowCollabSearchModal(false);
    setCollabSearchUsers([]);
    setCollabSearchDepts([]);
    setCollabSearchTerm('');
  };
  const toggleCollabCheck = (targetId, targetType) => {
    setCollabParticipants(prev => prev.map(p => {
      if (String(p.target_id) === String(targetId) && p.target_type === targetType) {
        return { ...p, checked: !p.checked };
      }
      return p;
    }));
  };
  const deleteCollabSelected = () => {
    setCollabParticipants(prev => prev.filter(p => !p.checked));
  };
  const deleteCollabAll = () => {
    setCollabParticipants([]);
  };
  const handleCollabConfirmOrgSelection = (targets) => {
    const normalized = (targets || []).map(t => ({
      target_type: t.target_type,
      target_id: String(t.target_id),
      target_name: t.target_name || '',
      disp_target_name: t.disp_target_name || t.target_name || '',
      checked: false
    }));
    setCollabParticipants(prev => {
      const prevKeyMap = new Map(prev.map(p => [makeParticipantKey(p), p]));
      normalized.forEach(t => {
        const key = makeParticipantKey(t);
        if (!prevKeyMap.has(key)) prevKeyMap.set(key, t);
      });
      return Array.from(prevKeyMap.values());
    });
  };

  const isShareScheduleTab = activeTab === 'shareSchedule';
  const isMeetingScheduleTab = activeTab === 'meetingSchedule';

  // 공유/회의일정 저장
  const handleCollabSubmit = async (e) => {
    e.preventDefault();
    if (isCollabSubmitting) return;
    if (!collabTitle.trim()) { alert('제목을 입력하세요.'); return; }
    if (!collabStart || !collabEnd) { alert('일정일시를 선택하세요.'); return; }
    if (collabEnd <= collabStart) { alert('종료일시는 시작일시보다 늦어야 합니다.'); return; }
    const collabType = isMeetingScheduleTab ? 'M' : 'S';
    const hasTargets = collabParticipants.some(
      p => p.target_type === 'user' || p.target_type === 'dept'
    );
    if (!hasTargets) {
      alert(collabType === 'M' ? '회의는 반드시 참석자를 등록해야 합니다.' : '공유일정은 반드시 참석자를 등록해야 합니다.');
      return;
    }
    setIsCollabSubmitting(true);
    try {
      const dedupMap = new Map();
      collabParticipants.forEach(p => {
        const key = makeParticipantKey(p);
        if (!dedupMap.has(key)) dedupMap.set(key, p);
      });
      let payloadParticipants = Array.from(dedupMap.values()).map(t => ({
        target_from: collabType,
        target_type: t.target_type,
        target_id: String(t.target_id),
        target_name: t.target_name || '',
        disp_target_name: t.disp_target_name || t.target_name || ''
      }));
      if (collabType === 'M') {
        payloadParticipants = await expandParticipantsForMeeting(payloadParticipants, token);
        const meetingDedup = new Map();
        payloadParticipants.forEach(t => {
          const key = `${t.target_type}:${String(t.target_id)}`;
          if (!meetingDedup.has(key)) meetingDedup.set(key, t);
        });
        payloadParticipants = Array.from(meetingDedup.values());
        const currentUserId = String(user?.userid || '');
        if (currentUserId) {
          payloadParticipants = payloadParticipants.filter(
            t => !(t.target_type === 'user' && String(t.target_id) === currentUserId)
          );
        }
        payloadParticipants = payloadParticipants.map(t => ({
          ...t,
          target_type: 'user'
        }));
        if (payloadParticipants.length === 0) {
          alert('본인을 제외하면 참석자가 없습니다. 참석자를 다시 선택하세요.');
          return;
        }
      }
      const created = await createSchedule({
        title: collabTitle,
        writer_id: user?.userid,
        writer_name: user?.name,
        writer_post: userInfo?.position || null,
        dept_code: user?.dept_code || null,
        dept_name: userInfo?.dept_name || null,
        start_date: toDateText(collabStart),
        start_time: toTimeText(collabStart),
        end_date: toDateText(collabEnd),
        end_time: toTimeText(collabEnd),
        event_type: collabType,
        event_place: '',
        participants: payloadParticipants,
        content: '',
        status: 'register',
        deletedFileIds: []
      }, token);
      if (collabType === 'M' && created?.id) {
        if (collabSendMailYn === 'Y') {
          await sendMail(created.id, token);
        } else {
          await cancelMail(created.id, token);
        }
      }
      alert('등록되었습니다.');
      const now = new Date();
      setCollabTitle('');
      setCollabStart(roundToInterval(now, 10));
      setCollabEnd(roundToInterval(new Date(now.getTime() + 60 * 60 * 1000), 10));
      setCollabSendMailYn('N');
      setCollabSearchTerm('');
      setCollabSearchUsers([]);
      setCollabSearchDepts([]);
      setShowCollabSearchModal(false);
      setShowCollabOrgPopup(false);
      setCollabParticipants([]);
    } catch (err) {
      alert('일정 등록 중 오류가 발생했습니다.');
    } finally {
      setIsCollabSubmitting(false);
    }
  };

  return (
    <div className="card portal-card reservation-quick-card flex-grow-1">
      <div className="card-body" style={{ paddingBottom: "0.3rem" }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="card-title mb-0">빠른 일정 등록</h4>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => navigate('/schedule/calendar')}
          >
            일정보기
          </button>
        </div>
        <ul className="nav nav-pills bg-light rounded" role="tablist">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link${activeTab === 'schedule' ? ' active' : ''}`}
              onClick={() => handleTabChange('schedule')}
            >
              업무일정
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link${activeTab === 'shareSchedule' ? ' active' : ''}`}
              onClick={() => handleTabChange('shareSchedule')}
            >
              공유일정
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link${activeTab === 'meetingSchedule' ? ' active' : ''}`}
              onClick={() => handleTabChange('meetingSchedule')}
            >
              회의일정
            </button>
          </li>
        </ul>
        <div className="tab-content mt-3" style={{ minHeight: "245px" }}>
          {/* 업무일정 */}
          {activeTab === 'schedule' && (
            <form onSubmit={handleScheduleSubmit} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
              <div className="mb-2">
                <input
                  className="form-control"
                  type="text"
                  value={schedTitle}
                  onChange={e => setSchedTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                  placeholder="제목을 입력하세요."
                />
              </div>
              <div className="mb-3" style={{ marginTop: '16px' }}>
                <div className="d-flex align-items-center gap-2">
                  <CommonDatePicker
                    inputClassName="compact-date-input"
                    wrapperClassName="compact-date-wrapper"
                    value={schedStart}
                    onChange={setSchedStart}
                    placeholder="시작일시"
                    showTime={true}
                  />
                  <span>~</span>
                  <CommonDatePicker
                    inputClassName="compact-date-input"
                    wrapperClassName="compact-date-wrapper"
                    value={schedEnd}
                    onChange={setSchedEnd}
                    placeholder="종료일시"
                    showTime={true}
                    minDate={schedStart}
                  />
                </div>
              </div>
              <div className="mb-2" style={{ marginTop: '16px' }}>
                <select
                  className="form-select"
                  value={selectedDept?.dept_code || ''}
                  onChange={e => {
                    const dept = departments.find(d => d.dept_code === e.target.value);
                    setSelectedDept(dept || null);
                    if (dept) {
                      setSelectedTargets([{
                        target_id: dept.dept_code,
                        target_name: dept.dept_name,
                        target_type: 'dept',
                        disp_target_name: dept.full_dept_name
                      }]);
                    } else {
                      setSelectedTargets([]);
                    }
                  }}
                >
                  <option value="">부서를 선택하세요.</option>
                  {departments.map(d => (
                    <option key={d.dept_code} value={d.dept_code}>
                      {d.full_dept_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-center mt-3">
                <button type="submit" className="btn btn-success w-md">등록</button>
              </div>
            </form>
          )}
          {/* 공유/회의일정 */}
          {(isShareScheduleTab || isMeetingScheduleTab) && (
            <form onSubmit={handleCollabSubmit} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
              <div className="mb-2">
                <input
                  className="form-control"
                  type="text"
                  value={collabTitle}
                  onChange={e => setCollabTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                  placeholder="제목을 입력하세요."
                />
              </div>
              <div className="mb-3" style={{ marginTop: '16px' }}>
                <div className="d-flex align-items-center gap-2">
                  <CommonDatePicker
                    inputClassName="compact-date-input"
                    wrapperClassName="compact-date-wrapper"
                    style={isMeetingScheduleTab ? { width: '135px' } : {}}
                    value={collabStart}
                    onChange={setCollabStart}
                    placeholder="시작일시"
                    showTime={true}
                  />
                  <span>~</span>
                  <CommonDatePicker
                    inputClassName="compact-date-input"
                    wrapperClassName="compact-date-wrapper"
                    style={isMeetingScheduleTab ? { width: '135px' } : {}}
                    value={collabEnd}
                    onChange={setCollabEnd}
                    placeholder="종료일시"
                    showTime={true}
                    minDate={collabStart}
                  />
                  {isMeetingScheduleTab && (
                    <div className="d-flex align-items-center gap-1 ms-auto collab-mail-option" style={{ whiteSpace: 'nowrap' }}>
                      <label className="form-check mb-0 p-0" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="collabSendMailYn"
                          value="Y"
                          checked={collabSendMailYn === 'Y'}
                          onChange={e => setCollabSendMailYn(e.target.value)}
                          style={{ margin: 0, cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '12px' }}>발송</span>
                      </label>
                      <label className="form-check mb-0 p-0" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', cursor: 'pointer', marginLeft: '6px' }}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="collabSendMailYn"
                          value="N"
                          checked={collabSendMailYn === 'N'}
                          onChange={e => setCollabSendMailYn(e.target.value)}
                          style={{ margin: 0, cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '12px' }}>미발송</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-2">
                <div className="d-flex align-items-center gap-1">
                  <input
                    type="text"
                    className="form-control collab-search-input"
                    style={{ marginBottom: 0, width: '50%' }}
                    value={collabSearchTerm}
                    onChange={e => setCollabSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCollabSearch(collabSearchTerm);
                      }
                    }}
                    placeholder="참석자 검색"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => handleCollabSearch(collabSearchTerm)}
                  >
                    검색
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => {
                      setOrgTargetType('all');        // 👈 모드 설정 추가
                      setShowCollabOrgPopup(true);    // 👈 기존 팝업 열기
                    }}
                  >
                    조직도
                  </button>

                  <div className="collab-count-inline ms-2" style={{ whiteSpace: 'nowrap', fontSize: '13px', color: '#4e73df', fontWeight: 'bold' }}>Count: {collabParticipants.length}</div>
                </div>
              </div>
              <div className="mb-2">
                <div className="d-flex align-items-center gap-2 collab-attendee-row" style={{ marginTop: '16px' }}>
                  <div
                    className={`collab-attendee-box flex-grow-1 ${collabParticipants.length > 0 ? 'has-items' : 'is-empty'}`}
                    style={{
                      height: '73px',
                      overflowY: 'auto',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      padding: '4px 6px'
                    }}
                  >
                    {collabParticipants.length === 0 ? (
                      <div
                        className="text-muted collab-attendee-empty collab-empty-text"
                        style={{
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          width: '100%',
                          textAlign: 'center'
                        }}
                      >
                        참석자가 지정되지 않았습니다.
                      </div>
                    ) : (
                      collabParticipants.map(p => (
                        <label
                          key={`${p.target_type}-${p.target_id}`}
                          className="d-flex align-items-center gap-2 mb-0 collab-attendee-item"
                          style={{ cursor: 'pointer' }}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            style={{ width: '13px', height: '13px' }}
                            checked={Boolean(p.checked)}
                            onChange={() => toggleCollabCheck(p.target_id, p.target_type)}
                          />
                          <span style={{ fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {p.disp_target_name}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                  <div className="d-flex flex-column align-items-end gap-2 collab-attendee-actions">
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={deleteCollabSelected}>선택삭제</button>
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={deleteCollabAll}>전체삭제</button>
                  </div>
                </div>
              </div>
              <div className="text-center mt-3">
                <button type="submit" className="btn btn-success w-md" disabled={isCollabSubmitting}>
                  {isCollabSubmitting ? '등록중...' : '등록'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      {showCollabOrgPopup && (
        <OrgTreeSelect
          selectionMode="multiple"
          targetType={orgTargetType}
          initialSelected={collabParticipants}
          onConfirm={handleCollabConfirmOrgSelection}
          onCancel={() => setShowCollabOrgPopup(false)}
        />
      )}
      {showCollabSearchModal && createPortal(
        <div
          className="collab-search-modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div
            className="collab-search-modal card shadow-lg"
            style={{
              width: '420px',
              maxWidth: '90%',
              borderRadius: '12px',
              border: 'none',
              overflow: 'hidden'
            }}
          >
            <div className="card-header fw-bold text-center py-3" style={{ fontSize: '1.1rem', backgroundColor: '#f8f9fa' }}>
              검색결과
            </div>
            <div className="card-body p-3">
              <div className="collab-search-modal-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {collabSearchUsers.length === 0 && collabSearchDepts.length === 0 ? (
                  <div className="text-center py-3 text-muted" style={{ fontSize: '11px' }}>결과 없음</div>
                ) : (
                  <>
                    {collabSearchUsers.map(u => (
                      <label key={`modal-user-${u.userid}`} className="d-block mb-1" style={{ fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          style={{ width: '13px', height: '13px', cursor: 'pointer', marginTop: 0 }}
                          checked={Boolean(u.checked)}
                          onChange={() => toggleSearchModalItem('user', u.userid)}
                        />
                        <span className="fw-bold">{u.name}</span>
                        <span className="text-muted ms-1">/ {u.position}</span>
                        {u.dept_name && <span className="ms-1" style={{ color: '#2b6cb0' }}>({u.dept_name})</span>}
                      </label>
                    ))}
                    {collabSearchDepts.map(d => (
                      <label key={`modal-dept-${d.dept_code}`} className="d-block mb-1" style={{ fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          style={{ width: '13px', height: '13px', cursor: 'pointer', marginTop: 0 }}
                          checked={Boolean(d.checked)}
                          onChange={() => toggleSearchModalItem('dept', d.dept_code)}
                        />
                        <span className="fw-bold" style={{ color: '#5a67d8' }}>{d.full_dept_name || d.dept_name}</span>
                      </label>
                    ))}
                  </>
                )}
              </div>
            </div>
            <div className="card-footer d-flex justify-content-end gap-2 p-3 bg-light">
              <button type="button" className="btn btn-primary px-4 fw-bold" onClick={handleConfirmCollabSearchModal}>확인</button>
              <button type="button" className="btn btn-outline-secondary px-4" onClick={() => setShowCollabSearchModal(false)}>취소</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default ScheduleCard;